<template>
  <div class="min-h-screen bg-[#070b14] text-slate-100 font-sans pb-14 selection:bg-cyan-400/20 selection:text-cyan-200">
    <div class="mx-auto max-w-[1800px] p-4 md:p-6 lg:p-8 space-y-6">

      <!-- HEADER -->
      <header class="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-950 to-cyan-950/40 p-5 md:p-7 shadow-2xl shadow-cyan-950/20">
        <div class="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl"></div>
        <div class="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-violet-500/10 blur-3xl"></div>

        <div class="relative flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
          <div class="space-y-2">
            <div class="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-cyan-300">
              <span class="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.9)]"></span>
              Developer console
            </div>
            <h1 class="text-2xl md:text-3xl font-black tracking-tight text-white">
              Observabilité temps réel
            </h1>
            <p class="max-w-2xl text-sm leading-relaxed text-slate-400">
              Flux WebSocket, appareils connectés, état du serveur et de MongoDB, rapport d’activité sur les 30 derniers jours.
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <span class="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-bold uppercase tracking-wider"
                  :class="socketConnected ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300' : 'border-rose-400/20 bg-rose-400/10 text-rose-300'">
              <span class="h-1.5 w-1.5 rounded-full" :class="socketConnected ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'"></span>
              WebSocket {{ socketConnected ? 'live' : 'offline' }}
            </span>
            <span class="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-bold uppercase tracking-wider"
                  :class="databaseTone">
              <span class="h-1.5 w-1.5 rounded-full" :class="databaseDot"></span>
              BDD {{ overview?.database?.status || '—' }}
            </span>
            <span class="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-300">
              <span class="h-1.5 w-1.5 rounded-full bg-cyan-400"></span>
              {{ overview?.devices?.online ?? '—' }} devices online
            </span>

            <button @click="refreshData"
                    :disabled="refreshing"
                    class="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-200 hover:bg-white/[0.1] transition-all disabled:opacity-50">
              <svg class="h-3.5 w-3.5" :class="{ 'animate-spin': refreshing }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser
            </button>
          </div>
        </div>

        <div class="relative mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-medium text-slate-500">
          <span>Dernière synchro : <strong class="text-slate-300">{{ lastUpdatedLabel }}</strong></span>
          <span>Node : <strong class="text-slate-300">{{ overview?.server?.nodeVersion || '—' }}</strong></span>
          <span>Uptime : <strong class="text-slate-300">{{ formatUptime(overview?.server?.uptimeSeconds) }}</strong></span>
          <span>PID : <strong class="text-slate-300">{{ overview?.server?.pid || '—' }}</strong></span>
        </div>
      </header>

      <!-- TABS -->
      <nav class="flex flex-wrap gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
        <button v-for="tab in tabs" :key="tab.id"
                @click="activeTab = tab.id"
                class="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-[10px] font-black uppercase tracking-wider transition-all"
                :class="activeTab === tab.id
                  ? 'bg-cyan-400/15 text-cyan-200 shadow-sm shadow-cyan-500/10 ring-1 ring-cyan-400/20'
                  : 'text-slate-400 hover:bg-white/[0.05] hover:text-slate-200'">
          <span v-html="tab.icon" class="dev-icon h-4 w-4"></span>
          {{ tab.label }}
          <span v-if="tab.id === 'live'" class="rounded-full bg-cyan-400/15 px-1.5 py-0.5 text-[9px] text-cyan-300">
            {{ filteredEvents.length }}
          </span>
        </button>
      </nav>

      <div v-if="error" class="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
        {{ error }}
      </div>

      <div v-if="loading && !overview" class="flex min-h-[420px] items-center justify-center rounded-3xl border border-white/10 bg-white/[0.03]">
        <div class="flex flex-col items-center gap-3 text-slate-400">
          <span class="h-8 w-8 animate-spin rounded-full border-2 border-cyan-400/30 border-t-cyan-400"></span>
          <span class="text-xs font-bold uppercase tracking-widest">Chargement de la console...</span>
        </div>
      </div>

      <template v-else>
        <!-- OVERVIEW -->
        <template v-if="activeTab === 'overview'">
          <div class="grid sm:grid-cols-2 xl:grid-cols-6 gap-3">
            <div v-for="kpi in kpiCards" :key="kpi.label"
                 class="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-4 shadow-lg shadow-black/10">
              <div class="flex items-start justify-between gap-2">
                <span class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">{{ kpi.label }}</span>
                <span class="rounded-lg border px-1.5 py-0.5 text-[9px] font-black" :class="accentClass(kpi.tone)">
                  {{ kpi.badge }}
                </span>
              </div>
              <div class="mt-3 text-xl font-black tracking-tight text-white tabular-nums">{{ kpi.value }}</div>
              <div class="mt-1 text-[10px] font-medium text-slate-500">{{ kpi.detail }}</div>
            </div>
          </div>

          <div class="grid xl:grid-cols-12 gap-4">
            <!-- Serveur -->
            <section class="xl:col-span-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Serveur" subtitle="Process Node.js" icon="server" tone="emerald" />
              <div class="mt-4 space-y-3 text-xs">
                <InfoRow label="Statut" :value="overview?.server?.status || '—'" :tone="overview?.server?.status === 'online' ? 'emerald' : 'rose'" />
                <InfoRow label="Environnement" :value="overview?.server?.environment || '—'" />
                <InfoRow label="Plateforme" :value="`${overview?.server?.platform || '—'} / ${overview?.server?.arch || '—'}`" />
                <InfoRow label="Node" :value="overview?.server?.nodeVersion || '—'" />
                <InfoRow label="CPU" :value="`${overview?.server?.cpuCount || 0} cœurs`" />
                <InfoRow label="Load average" :value="formatLoad(overview?.server?.loadAverage)" />

                <div class="pt-2">
                  <div class="mb-1 flex justify-between text-[10px] font-bold text-slate-400">
                    <span>Mémoire heap</span>
                    <span>{{ formatBytes(overview?.server?.memory?.heapUsed) }} / {{ formatBytes(overview?.server?.memory?.heapTotal) }}</span>
                  </div>
                  <div class="h-2 overflow-hidden rounded-full bg-white/10">
                    <div class="h-full rounded-full bg-emerald-400 transition-all" :style="{ width: heapPercent + '%' }"></div>
                  </div>
                </div>
                <div class="pt-1">
                  <div class="mb-1 flex justify-between text-[10px] font-bold text-slate-400">
                    <span>Mémoire système</span>
                    <span>{{ formatBytes(overview?.server?.totalMemory - overview?.server?.freeMemory) }} / {{ formatBytes(overview?.server?.totalMemory) }}</span>
                  </div>
                  <div class="h-2 overflow-hidden rounded-full bg-white/10">
                    <div class="h-full rounded-full bg-cyan-400 transition-all" :style="{ width: systemMemoryPercent + '%' }"></div>
                  </div>
                </div>
              </div>
            </section>

            <!-- BDD -->
            <section class="xl:col-span-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="MongoDB" subtitle="Connexion & volumétrie" icon="database" tone="cyan" />
              <div class="mt-4 space-y-3 text-xs">
                <InfoRow label="État" :value="overview?.database?.status || '—'" :tone="overview?.database?.status === 'connected' ? 'emerald' : 'rose'" />
                <InfoRow label="Base" :value="overview?.database?.name || '—'" />
                <InfoRow label="Hôte" :value="overview?.database?.host || '—'" />
                <InfoRow label="Latence ping" :value="overview?.database?.pingMs != null ? `${overview.database.pingMs} ms` : '—'" />
                <InfoRow label="Collections" :value="overview?.database?.collections ?? '—'" />
                <InfoRow label="Documents" :value="formatNumber(overview?.database?.objects)" />
                <InfoRow label="Taille données" :value="formatBytes(overview?.database?.dataSize)" />
                <InfoRow label="Stockage" :value="formatBytes(overview?.database?.storageSize)" />

                <div class="grid grid-cols-2 gap-2 pt-2">
                  <MiniStat label="Agences" :value="overview?.database?.counts?.agencies" />
                  <MiniStat label="Devices" :value="overview?.database?.counts?.devices" />
                  <MiniStat label="Tickets" :value="overview?.database?.counts?.tickets" />
                  <MiniStat label="Files" :value="overview?.database?.counts?.queues" />
                  <MiniStat label="Users" :value="overview?.database?.counts?.users" />
                  <MiniStat label="En attente" :value="overview?.database?.counts?.waitingTickets" />
                </div>
                <p v-if="overview?.database?.error" class="rounded-lg border border-rose-400/20 bg-rose-400/10 p-2 text-[10px] text-rose-300">
                  {{ overview.database.error }}
                </p>
              </div>
            </section>

            <!-- WebSocket -->
            <section class="xl:col-span-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="WebSocket" subtitle="Connexions & événements" icon="signal" tone="violet" />
              <div class="mt-4 grid grid-cols-2 gap-2">
                <MiniStat label="Sockets" :value="overview?.websocket?.connectedSockets" />
                <MiniStat label="Dev listeners" :value="overview?.websocket?.superAdminListeners" />
                <MiniStat label="Événements stockés" :value="eventStats?.stored ?? events.length" />
                <MiniStat label="Événements / min" :value="eventsPerMinute" />
              </div>

              <div class="mt-4 space-y-2">
                <div class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Connexions par source</div>
                <div v-if="socketSourceEntries.length === 0" class="text-[11px] text-slate-500">Aucune connexion active.</div>
                <div v-for="[source, count] in socketSourceEntries" :key="source"
                     class="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2 text-[11px]">
                  <span class="font-mono text-slate-300">{{ source }}</span>
                  <span class="font-black text-cyan-300">{{ count }}</span>
                </div>
              </div>

              <div class="mt-4 space-y-2">
                <div class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Top événements</div>
                <div v-if="!eventStats?.topEvents?.length" class="text-[11px] text-slate-500">En attente de trafic…</div>
                <div v-for="item in eventStats?.topEvents?.slice(0, 5)" :key="item.event"
                     class="flex items-center justify-between gap-2 text-[11px]">
                  <span class="truncate font-mono text-slate-300">{{ item.event }}</span>
                  <span class="shrink-0 rounded-md bg-violet-400/10 px-1.5 py-0.5 font-black text-violet-300">{{ item.count }}</span>
                </div>
              </div>
            </section>
          </div>

          <div class="grid xl:grid-cols-2 gap-4">
            <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Tickets — 30 jours" subtitle="Volume quotidien" icon="chart" tone="cyan" />
              <div v-if="report" class="mt-3">
                <VueApexCharts type="area" height="260" :options="trendOptions" :series="trendSeries" />
              </div>
              <div v-else class="mt-3 flex h-[260px] items-center justify-center text-xs text-slate-500">Chargement du rapport…</div>
            </section>

            <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Parc connecté" subtitle="Répartition par type" icon="devices" tone="emerald" />
              <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <VueApexCharts type="donut" height="240" :options="deviceTypeOptions" :series="deviceTypeSeries" />
                <div class="space-y-2">
                  <div v-for="item in deviceTypeCards" :key="item.type"
                       class="rounded-xl border border-white/5 bg-white/[0.03] p-3">
                    <div class="flex items-center justify-between">
                      <span class="text-[10px] font-black uppercase tracking-wider text-slate-400">{{ item.type }}</span>
                      <span class="rounded-md px-1.5 py-0.5 text-[9px] font-black" :class="item.online > 0 ? 'bg-emerald-400/10 text-emerald-300' : 'bg-white/5 text-slate-500'">
                        {{ item.online }} / {{ item.total }}
                      </span>
                    </div>
                    <div class="mt-1 text-lg font-black text-white">{{ item.total }}</div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </template>

        <!-- LIVE -->
        <template v-if="activeTab === 'live'">
          <div class="grid xl:grid-cols-4 gap-4">
            <section class="xl:col-span-3 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
              <div class="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4">
                <div>
                  <h2 class="text-sm font-black text-white">Flux WebSocket en direct</h2>
                  <p class="text-[10px] text-slate-500">Événements entrants, sortants et système de tous les appareils.</p>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <select v-model="filter.direction"
                          class="rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1.5 text-[10px] font-bold text-slate-300 outline-none">
                    <option value="all">Tout</option>
                    <option value="in">Entrant</option>
                    <option value="out">Sortant</option>
                    <option value="system">Système</option>
                  </select>
                  <input v-model="filter.search"
                         placeholder="Rechercher event, source, payload…"
                         class="w-52 rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1.5 text-[10px] text-slate-300 outline-none placeholder:text-slate-600 focus:border-cyan-400/40" />
                  <button @click="paused = !paused"
                          class="rounded-lg border px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all"
                          :class="paused ? 'border-amber-400/20 bg-amber-400/10 text-amber-300' : 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300'">
                    {{ paused ? 'Reprendre' : 'Pause' }}
                  </button>
                  <button @click="clearEvents"
                          class="rounded-lg border border-rose-400/20 bg-rose-400/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-rose-300 hover:bg-rose-400/15 transition-all">
                    Vider
                  </button>
                </div>
              </div>

              <div class="max-h-[640px] overflow-y-auto custom-scrollbar">
                <div v-if="filteredEvents.length === 0" class="flex h-72 flex-col items-center justify-center gap-2 text-slate-500">
                  <span class="text-xs font-bold uppercase tracking-widest">Aucun événement</span>
                  <span class="text-[10px]">Les événements apparaîtront dès qu’un client émettra sur le WebSocket.</span>
                </div>

                <div v-for="entry in filteredEvents" :key="entry.id"
                     class="border-b border-white/5 px-4 py-3 hover:bg-white/[0.025] transition-colors">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="rounded-md border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider" :class="directionClass(entry.direction)">
                      {{ directionLabel(entry.direction) }}
                    </span>
                    <span class="font-mono text-xs font-bold text-slate-100">{{ entry.event }}</span>
                    <span class="rounded-md bg-white/5 px-1.5 py-0.5 font-mono text-[9px] text-slate-400">{{ entry.source }}</span>
                    <span class="ml-auto text-[10px] tabular-nums text-slate-500">{{ formatTime(entry.timestamp) }}</span>
                    <button @click="toggleEvent(entry.id)"
                            class="rounded-md border border-white/10 px-1.5 py-0.5 text-[9px] font-bold text-slate-400 hover:text-cyan-300 hover:border-cyan-400/30 transition-all">
                      {{ expandedEventIds.includes(entry.id) ? 'Fermer' : 'Payload' }}
                    </button>
                  </div>
                  <div v-if="entry.socketId" class="mt-1 font-mono text-[9px] text-slate-600">socket: {{ entry.socketId }}</div>
                  <pre v-if="expandedEventIds.includes(entry.id)"
                       class="mt-2 max-h-64 overflow-auto rounded-lg border border-white/5 bg-black/40 p-3 text-[10px] leading-relaxed text-cyan-200/80">{{ prettyData(entry.data) }}</pre>
                </div>
              </div>
            </section>

            <aside class="space-y-4">
              <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <SectionTitle title="Trafic" subtitle="Fenêtre glissante" icon="signal" tone="violet" />
                <div class="mt-4 grid grid-cols-2 gap-2">
                  <MiniStat label="Événements / min" :value="eventsPerMinute" />
                  <MiniStat label="Affichés" :value="filteredEvents.length" />
                  <MiniStat label="Stockés" :value="events.length" />
                  <MiniStat label="Sockets" :value="overview?.websocket?.connectedSockets" />
                </div>
              </section>

              <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <SectionTitle title="Légende" subtitle="Types de flux" icon="info" tone="amber" />
                <div class="mt-4 space-y-2 text-[11px]">
                  <LegendRow color="cyan" label="Entrant" description="Client → serveur" />
                  <LegendRow color="violet" label="Sortant" description="Serveur → client" />
                  <LegendRow color="amber" label="Système" description="Connexion / déconnexion" />
                </div>
              </section>

              <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <SectionTitle title="Derniers événements" subtitle="Top occurrences" icon="chart" tone="cyan" />
                <div class="mt-4 space-y-2">
                  <div v-for="item in eventStats?.topEvents?.slice(0, 8)" :key="item.event"
                       class="flex items-center justify-between gap-2 text-[11px]">
                    <span class="truncate font-mono text-slate-300">{{ item.event }}</span>
                    <span class="shrink-0 font-black text-cyan-300">{{ item.count }}</span>
                  </div>
                  <div v-if="!eventStats?.topEvents?.length" class="text-[11px] text-slate-500">Aucune donnée.</div>
                </div>
              </section>
            </aside>
          </div>
        </template>

        <!-- DEVICES -->
        <template v-if="activeTab === 'devices'">
          <div class="grid sm:grid-cols-3 gap-3">
            <div v-for="item in deviceTypeCards" :key="item.type"
                 class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div class="flex items-center justify-between">
                <span class="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">{{ item.type }}</span>
                <span class="rounded-md px-2 py-0.5 text-[9px] font-black" :class="item.online > 0 ? 'bg-emerald-400/10 text-emerald-300' : 'bg-white/5 text-slate-500'">
                  {{ item.online }} en ligne
                </span>
              </div>
              <div class="mt-3 flex items-end gap-2">
                <span class="text-3xl font-black text-white">{{ item.online }}</span>
                <span class="pb-1 text-xs font-bold text-slate-500">/ {{ item.total }}</span>
              </div>
              <div class="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                <div class="h-full rounded-full bg-emerald-400" :style="{ width: item.total ? `${Math.round((item.online / item.total) * 100)}%` : '0%' }"></div>
              </div>
            </div>
          </div>

          <section class="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
            <div class="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-5">
              <SectionTitle title="Appareils connectés" subtitle="Connexions actives en simultané" icon="devices" tone="emerald" />
              <span class="rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-300">
                {{ overview?.devices?.list?.length || 0 }} affichés
              </span>
            </div>

            <div v-if="!overview?.devices?.list?.length" class="flex h-48 items-center justify-center text-xs text-slate-500">
              Aucun appareil connecté pour le moment.
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-left text-xs">
                <thead class="bg-white/[0.03] text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">
                  <tr>
                    <th class="px-5 py-3">Appareil</th>
                    <th class="px-5 py-3">Type</th>
                    <th class="px-5 py-3">Agence</th>
                    <th class="px-5 py-3">Dernier ping</th>
                    <th class="px-5 py-3">Santé</th>
                    <th class="px-5 py-3">Version</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5">
                  <tr v-for="device in overview.devices.list" :key="device.id" class="hover:bg-white/[0.025] transition-colors">
                    <td class="px-5 py-3">
                      <div class="font-bold text-slate-100">{{ device.name }}</div>
                      <div class="font-mono text-[9px] text-slate-500">{{ device.identifier }}</div>
                    </td>
                    <td class="px-5 py-3">
                      <span class="rounded-md border border-cyan-400/20 bg-cyan-400/10 px-1.5 py-0.5 text-[9px] font-black text-cyan-300">{{ device.type }}</span>
                    </td>
                    <td class="px-5 py-3 text-slate-300">{{ device.agencyName }}</td>
                    <td class="px-5 py-3 text-slate-400">{{ formatRelative(device.lastPing) }}</td>
                    <td class="px-5 py-3">
                      <span class="inline-flex items-center gap-1.5 text-[10px] font-bold" :class="healthClass(device.health)">
                        <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
                        {{ device.health }}
                      </span>
                    </td>
                    <td class="px-5 py-3 font-mono text-[10px] text-slate-400">{{ device.appVersion || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>

        <!-- DATABASE / SERVER -->
        <template v-if="activeTab === 'database'">
          <div class="grid xl:grid-cols-2 gap-4">
            <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Serveur Node.js" subtitle="Processus applicatif" icon="server" tone="emerald" />
              <div class="mt-4 grid sm:grid-cols-2 gap-2">
                <InfoCard label="Uptime" :value="formatUptime(overview?.server?.uptimeSeconds)" />
                <InfoCard label="Environnement" :value="overview?.server?.environment || '—'" />
                <InfoCard label="Node" :value="overview?.server?.nodeVersion || '—'" />
                <InfoCard label="PID" :value="overview?.server?.pid || '—'" />
                <InfoCard label="Plateforme" :value="`${overview?.server?.platform || '—'} / ${overview?.server?.arch || '—'}`" />
                <InfoCard label="CPU" :value="`${overview?.server?.cpuCount || 0} cœurs`" />
                <InfoCard label="RSS" :value="formatBytes(overview?.server?.memory?.rss)" />
                <InfoCard label="Heap utilisé" :value="formatBytes(overview?.server?.memory?.heapUsed)" />
              </div>
              <div class="mt-4 space-y-3">
                <ProgressBar label="Heap Node" :value="overview?.server?.memory?.heapUsed" :max="overview?.server?.memory?.heapTotal" color="emerald" />
                <ProgressBar label="Mémoire système" :value="(overview?.server?.totalMemory || 0) - (overview?.server?.freeMemory || 0)" :max="overview?.server?.totalMemory" color="cyan" />
              </div>
            </section>

            <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Base de données" subtitle="MongoDB" icon="database" tone="cyan" />
              <div class="mt-4 grid sm:grid-cols-2 gap-2">
                <InfoCard label="État" :value="overview?.database?.status || '—'" :tone="overview?.database?.status === 'connected' ? 'emerald' : 'rose'" />
                <InfoCard label="Latence ping" :value="overview?.database?.pingMs != null ? `${overview.database.pingMs} ms` : '—'" />
                <InfoCard label="Base" :value="overview?.database?.name || '—'" />
                <InfoCard label="Hôte" :value="overview?.database?.host || '—'" />
                <InfoCard label="Collections" :value="overview?.database?.collections ?? '—'" />
                <InfoCard label="Index" :value="overview?.database?.indexes ?? '—'" />
                <InfoCard label="Documents" :value="formatNumber(overview?.database?.objects)" />
                <InfoCard label="Taille données" :value="formatBytes(overview?.database?.dataSize)" />
              </div>
              <div class="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                <MiniStat label="Agences" :value="overview?.database?.counts?.agencies" />
                <MiniStat label="Devices" :value="overview?.database?.counts?.devices" />
                <MiniStat label="Tickets" :value="overview?.database?.counts?.tickets" />
                <MiniStat label="En attente" :value="overview?.database?.counts?.waitingTickets" />
                <MiniStat label="Appelés" :value="overview?.database?.counts?.calledTickets" />
                <MiniStat label="Files" :value="overview?.database?.counts?.queues" />
                <MiniStat label="Users" :value="overview?.database?.counts?.users" />
                <MiniStat label="Stockage" :value="formatBytes(overview?.database?.storageSize)" />
              </div>
            </section>
          </div>

          <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <SectionTitle title="Diagnostic brut" subtitle="Réponse /developer/overview" icon="info" tone="violet" />
            <pre class="mt-4 max-h-[420px] overflow-auto rounded-xl border border-white/5 bg-black/40 p-4 text-[10px] leading-relaxed text-cyan-200/80 custom-scrollbar">{{ JSON.stringify(overview, null, 2) }}</pre>
          </section>
        </template>

        <!-- REPORT -->
        <template v-if="activeTab === 'report'">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div class="flex flex-wrap items-center gap-3">
                <h2 class="text-lg font-black text-white">Rapport d’activité — {{ reportDays }} derniers jours</h2>
                <div class="flex gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-1">
                  <button v-for="days in [7, 30, 90]" :key="days"
                          @click="setReportDays(days)"
                          class="rounded-md px-2 py-1 text-[9px] font-black uppercase tracking-wider transition-all"
                          :class="reportDays === days ? 'bg-cyan-400/15 text-cyan-300' : 'text-slate-500 hover:text-slate-200'">
                    {{ days }} j
                  </button>
                </div>
              </div>
              <p class="text-[10px] text-slate-500">Généré le {{ formatDateTime(report?.generatedAt) }}</p>
            </div>
            <button @click="exportReportCsv"
                    class="inline-flex items-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3.5 py-2 text-[10px] font-black uppercase tracking-wider text-cyan-300 hover:bg-cyan-400/15 transition-all">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v12m0 0l4-4m-4 4l-4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
              </svg>
              Exporter CSV
            </button>
          </div>

          <div class="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Tickets créés</div>
              <div class="mt-2 text-3xl font-black text-white tabular-nums">{{ report?.totals?.total ?? '—' }}</div>
              <div class="mt-1 text-[10px] text-slate-500">sur {{ report?.days || 30 }} jours</div>
            </div>
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Moyenne / jour</div>
              <div class="mt-2 text-3xl font-black text-cyan-300 tabular-nums">{{ report?.totals?.averagePerDay ?? '—' }}</div>
              <div class="mt-1 text-[10px] text-slate-500">tickets par jour</div>
            </div>
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Taux de traitement</div>
              <div class="mt-2 text-3xl font-black text-emerald-300 tabular-nums">{{ report?.totals?.completionRate ?? '—' }}%</div>
              <div class="mt-1 text-[10px] text-slate-500">{{ report?.totals?.done ?? 0 }} tickets traités</div>
            </div>
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div class="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Encore en attente</div>
              <div class="mt-2 text-3xl font-black text-amber-300 tabular-nums">{{ report?.totals?.waiting ?? '—' }}</div>
              <div class="mt-1 text-[10px] text-slate-500">tickets WAITING</div>
            </div>
          </div>

          <div class="grid xl:grid-cols-12 gap-4">
            <section class="xl:col-span-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Évolution des tickets" subtitle="30 derniers jours" icon="chart" tone="cyan" />
              <div class="mt-3">
                <VueApexCharts type="area" height="320" :options="trendOptions" :series="trendSeries" />
              </div>
            </section>

            <section class="xl:col-span-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Répartition par statut" subtitle="Sur la période" icon="info" tone="violet" />
              <div class="mt-3">
                <VueApexCharts type="donut" height="320" :options="statusOptions" :series="statusSeries" />
              </div>
            </section>

            <section class="xl:col-span-7 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Top agences" subtitle="Volume de tickets" icon="devices" tone="emerald" />
              <div class="mt-3">
                <VueApexCharts type="bar" height="300" :options="agencyOptions" :series="agencySeries" />
              </div>
            </section>

            <section class="xl:col-span-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Répartition horaire" subtitle="Heure de création" icon="signal" tone="amber" />
              <div class="mt-3">
                <VueApexCharts type="bar" height="300" :options="hourlyOptions" :series="hourlySeries" />
              </div>
            </section>
          </div>

          <div class="grid xl:grid-cols-2 gap-4">
            <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Top files d’attente" subtitle="Sur 30 jours" icon="chart" tone="cyan" />
              <div class="mt-4 space-y-2">
                <div v-for="(queue, index) in report?.topQueues || []" :key="queue.queueId || index"
                     class="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5">
                  <span class="flex h-6 w-6 items-center justify-center rounded-lg bg-cyan-400/10 text-[10px] font-black text-cyan-300">{{ index + 1 }}</span>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-bold text-slate-200">{{ queue.prefix ? `${queue.prefix} — ` : '' }}{{ queue.queueName }}</div>
                  </div>
                  <span class="font-black text-cyan-300">{{ queue.count }}</span>
                </div>
                <div v-if="!report?.topQueues?.length" class="text-[11px] text-slate-500">Aucune donnée sur la période.</div>
              </div>
            </section>

            <section class="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <SectionTitle title="Top agences" subtitle="Sur 30 jours" icon="devices" tone="emerald" />
              <div class="mt-4 space-y-2">
                <div v-for="(agency, index) in report?.topAgencies || []" :key="agency.agencyId || index"
                     class="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5">
                  <span class="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-400/10 text-[10px] font-black text-emerald-300">{{ index + 1 }}</span>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-bold text-slate-200">{{ agency.agencyName }}</div>
                  </div>
                  <span class="font-black text-emerald-300">{{ agency.count }}</span>
                </div>
                <div v-if="!report?.topAgencies?.length" class="text-[11px] text-slate-500">Aucune donnée sur la période.</div>
              </div>
            </section>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted, onUnmounted, h, defineComponent } from 'vue'
import { io } from 'socket.io-client'
import VueApexCharts from 'vue3-apexcharts'

const API_BASE = 'https://api.datafuse.fr/api/super-admin'
const WS_URL = 'https://api.datafuse.fr'

const overview = ref(null)
const report = ref(null)
const reportDays = ref(30)
const events = ref([])
const eventStats = ref(null)
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const activeTab = ref('overview')
const socketConnected = ref(false)
const socket = ref(null)
const paused = ref(false)
const filter = reactive({ direction: 'all', search: '' })
const expandedEventIds = ref([])
const now = ref(Date.now())
const lastUpdated = ref(null)

let overviewTimer = null
let reportTimer = null
let eventsFallbackTimer = null
let clockTimer = null

const tabs = [
  { id: 'overview', label: 'Vue d’ensemble', icon: icon('dashboard') },
  { id: 'live', label: 'Flux live', icon: icon('signal') },
  { id: 'devices', label: 'Appareils', icon: icon('devices') },
  { id: 'database', label: 'Serveur & BDD', icon: icon('server') },
  { id: 'report', label: 'Rapport 30 jours', icon: icon('chart') }
]

function icon(name) {
  const icons = {
    dashboard: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>',
    signal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 18v-4M8 18v-8M12 18V6M16 18v-5M20 18v-2"/></svg>',
    devices: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
    server: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="8" x="2" y="2" rx="2"/><rect width="20" height="8" x="2" y="14" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>',
    chart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 16l4-5 4 3 5-7"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>'
  }
  return icons[name] || icons.info
}

const toneClasses = {
  cyan: 'border-cyan-400/20 bg-cyan-400/10 text-cyan-300',
  emerald: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300',
  violet: 'border-violet-400/20 bg-violet-400/10 text-violet-300',
  amber: 'border-amber-400/20 bg-amber-400/10 text-amber-300',
  rose: 'border-rose-400/20 bg-rose-400/10 text-rose-300'
}

const accentClass = (tone) => toneClasses[tone] || toneClasses.cyan

/* ==========================================================
   COMPOSANTS LOCAUX (render functions, sans runtime compiler)
   ========================================================== */

const SectionTitle = defineComponent({
  props: {
    title: String,
    subtitle: String,
    icon: String,
    tone: { type: String, default: 'cyan' }
  },
  setup(props) {
    return () => h('div', { class: 'flex items-center gap-3' }, [
      h('div', {
        class: `dev-icon flex h-8 w-8 items-center justify-center rounded-xl border ${toneClasses[props.tone] || toneClasses.cyan}`,
        innerHTML: icon(props.icon)
      }),
      h('div', {}, [
        h('h2', { class: 'text-sm font-black text-white' }, props.title),
        h('p', { class: 'text-[10px] text-slate-500' }, props.subtitle)
      ])
    ])
  }
})

const MiniStat = defineComponent({
  props: ['label', 'value'],
  setup(props) {
    return () => h('div', { class: 'rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5' }, [
      h('div', { class: 'text-[9px] font-black uppercase tracking-wider text-slate-500' }, props.label),
      h('div', { class: 'mt-1 text-sm font-black text-white tabular-nums' }, String(props.value ?? '—'))
    ])
  }
})

const InfoRow = defineComponent({
  props: ['label', 'value', 'tone'],
  setup(props) {
    const valueClass = () => props.tone === 'emerald'
      ? 'text-emerald-300'
      : props.tone === 'rose'
        ? 'text-rose-300'
        : 'text-slate-200'

    return () => h('div', { class: 'flex items-center justify-between gap-3' }, [
      h('span', { class: 'text-slate-500' }, props.label),
      h('span', { class: `text-right font-bold ${valueClass()}` }, String(props.value ?? '—'))
    ])
  }
})

const InfoCard = defineComponent({
  props: ['label', 'value', 'tone'],
  setup(props) {
    const valueClass = () => props.tone === 'emerald'
      ? 'text-emerald-300'
      : props.tone === 'rose'
        ? 'text-rose-300'
        : 'text-white'

    return () => h('div', { class: 'rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5' }, [
      h('div', { class: 'text-[9px] font-black uppercase tracking-wider text-slate-500' }, props.label),
      h('div', { class: `mt-1 text-xs font-black ${valueClass()}` }, String(props.value ?? '—'))
    ])
  }
})

const ProgressBar = defineComponent({
  props: ['label', 'value', 'max', 'color'],
  setup(props) {
    const percent = () => {
      const max = Number(props.max) || 0
      const value = Number(props.value) || 0
      return max > 0 ? Math.max(0, Math.min(100, Math.round((value / max) * 100))) : 0
    }
    const barClass = () => props.color === 'cyan' ? 'bg-cyan-400' : 'bg-emerald-400'

    return () => h('div', {}, [
      h('div', { class: 'mb-1 flex justify-between text-[10px] font-bold text-slate-400' }, [
        h('span', {}, props.label),
        h('span', {}, `${percent()}%`)
      ]),
      h('div', { class: 'h-2 overflow-hidden rounded-full bg-white/10' }, [
        h('div', { class: `h-full rounded-full transition-all ${barClass()}`, style: { width: `${percent()}%` } })
      ])
    ])
  }
})

const LegendRow = defineComponent({
  props: ['color', 'label', 'description'],
  setup(props) {
    const colorClass = () => ({
      cyan: 'bg-cyan-400',
      violet: 'bg-violet-400',
      amber: 'bg-amber-400'
    }[props.color] || 'bg-slate-400')

    return () => h('div', { class: 'flex items-center gap-2' }, [
      h('span', { class: `h-1.5 w-1.5 rounded-full ${colorClass()}` }),
      h('span', { class: 'font-bold text-slate-300' }, props.label),
      h('span', { class: 'text-slate-500' }, `— ${props.description}`)
    ])
  }
})

/* ==========================================================
   FORMATTERS
   ========================================================== */

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('fr-FR').format(Number(value) || 0)
}

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return '—'
  const value = Number(bytes)
  if (value === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  return `${(value / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`
}

const formatUptime = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0)
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (days > 0) return `${days}j ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

const formatLoad = (load) => {
  if (!Array.isArray(load) || load.length === 0) return '—'
  return load.map(value => Number(value).toFixed(2)).join(' / ')
}

const formatTime = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const formatDateTime = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

const formatShortDate = (date) => {
  if (!date) return ''
  return new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

const formatRelative = (date) => {
  if (!date) return '—'
  const diff = Date.now() - new Date(date).getTime()
  if (diff < 60000) return 'À l’instant'
  if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`
  if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)} h`
  return `Il y a ${Math.floor(diff / 86400000)} j`
}

const directionLabel = (direction) => ({ in: 'Entrant', out: 'Sortant', system: 'Système' }[direction] || direction)

const directionClass = (direction) => ({
  in: 'border-cyan-400/20 bg-cyan-400/10 text-cyan-300',
  out: 'border-violet-400/20 bg-violet-400/10 text-violet-300',
  system: 'border-amber-400/20 bg-amber-400/10 text-amber-300'
}[direction] || 'border-white/10 bg-white/5 text-slate-300')

const healthClass = (health) => {
  if (health === 'healthy') return 'text-emerald-300'
  if (health === 'warning') return 'text-amber-300'
  if (health === 'critical') return 'text-rose-300'
  return 'text-slate-500'
}

const prettyData = (data) => {
  try {
    return JSON.stringify(data, null, 2)
  } catch (error) {
    return String(data)
  }
}

/* ==========================================================
   API
   ========================================================== */

const getToken = () => localStorage.getItem('superAdminToken')

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`
})

const fetchJson = async (path, options = {}) => {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) }
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} — ${response.statusText}`)
  }

  return response.json()
}

const loadOverview = async () => {
  const data = await fetchJson('/developer/overview')
  overview.value = data

  if (data.websocket?.events) {
    eventStats.value = data.websocket.events
  }
}

const loadReport = async () => {
  report.value = await fetchJson(`/developer/report?days=${reportDays.value}`)
}

const setReportDays = async (days) => {
  if (reportDays.value === days) return
  reportDays.value = days
  try {
    await loadReport()
  } catch (error) {
    error.value = 'Impossible de charger le rapport demandé.'
  }
}

const loadEvents = async () => {
  const data = await fetchJson('/developer/events?limit=300')
  if (Array.isArray(data.events)) {
    events.value = data.events
  }
  eventStats.value = data.stats || null
}

const loadAll = async (silent = false) => {
  if (!silent) loading.value = true
  error.value = ''

  const results = await Promise.allSettled([loadOverview(), loadReport(), loadEvents()])
  const failure = results.find(result => result.status === 'rejected')

  if (failure) {
    error.value = failure.reason?.message || 'Impossible de charger les données développeur.'
  } else {
    lastUpdated.value = new Date()
  }

  loading.value = false
}

const refreshData = async () => {
  if (refreshing.value) return
  refreshing.value = true
  await loadAll(true)
  refreshing.value = false
}

const clearEvents = async () => {
  if (!confirm('Vider l’historique des événements WebSocket ?')) return

  try {
    await fetchJson('/developer/events', { method: 'DELETE' })
    events.value = []
    eventStats.value = null
    expandedEventIds.value = []
  } catch (error) {
    error.value = 'Impossible de vider l’historique.'
  }
}

const toggleEvent = (id) => {
  if (expandedEventIds.value.includes(id)) {
    expandedEventIds.value = expandedEventIds.value.filter(item => item !== id)
  } else {
    expandedEventIds.value.push(id)
  }
}

/* ==========================================================
   WEBSOCKET
   ========================================================== */

const setupSocket = () => {
  const token = getToken()
  if (!token) return

  socket.value = io(WS_URL, {
    query: { token },
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    timeout: 20000
  })

  socket.value.on('connect', () => {
    socketConnected.value = true
  })

  socket.value.on('disconnect', () => {
    socketConnected.value = false
  })

  socket.value.on('connect_error', (err) => {
    socketConnected.value = false
    console.warn('[Developer] WebSocket error:', err.message)
  })

  socket.value.on('dev_event', (entry) => {
    if (!entry || paused.value) return

    events.value.unshift(entry)
    if (events.value.length > 400) {
      events.value.length = 400
    }

    eventStats.value = {
      ...(eventStats.value || {}),
      stored: events.value.length,
      lastEvent: entry
    }
  })
}

/* ==========================================================
   COMPUTED
   ========================================================== */

const databaseTone = computed(() => {
  if (overview.value?.database?.status === 'connected') return 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300'
  if (overview.value?.database) return 'border-rose-400/20 bg-rose-400/10 text-rose-300'
  return 'border-white/10 bg-white/[0.04] text-slate-300'
})

const databaseDot = computed(() => overview.value?.database?.status === 'connected' ? 'bg-emerald-400' : 'bg-rose-400')

const lastUpdatedLabel = computed(() => lastUpdated.value ? formatTime(lastUpdated.value) : '—')

const eventsPerMinute = computed(() => {
  const threshold = now.value - 60000
  return events.value.filter(entry => new Date(entry.timestamp).getTime() >= threshold).length
})

const filteredEvents = computed(() => {
  const search = filter.search.trim().toLowerCase()

  return events.value.filter(entry => {
    if (filter.direction !== 'all' && entry.direction !== filter.direction) return false
    if (!search) return true
    const haystack = `${entry.event} ${entry.source} ${JSON.stringify(entry.data || {})}`.toLowerCase()
    return haystack.includes(search)
  })
})

const socketSourceEntries = computed(() => Object.entries(overview.value?.websocket?.bySource || {}))

const heapPercent = computed(() => {
  const used = Number(overview.value?.server?.memory?.heapUsed) || 0
  const total = Number(overview.value?.server?.memory?.heapTotal) || 0
  return total > 0 ? Math.max(0, Math.min(100, Math.round((used / total) * 100))) : 0
})

const systemMemoryPercent = computed(() => {
  const total = Number(overview.value?.server?.totalMemory) || 0
  const free = Number(overview.value?.server?.freeMemory) || 0
  return total > 0 ? Math.max(0, Math.min(100, Math.round(((total - free) / total) * 100))) : 0
})

const deviceTypeCards = computed(() => {
  const byType = overview.value?.devices?.byType || {}
  const order = ['KIOSK', 'SCREEN', 'PRINTER']

  return Object.entries(byType)
    .map(([type, values]) => ({
      type,
      total: Number(values?.total) || 0,
      online: Number(values?.online) || 0,
      offline: Number(values?.offline) || 0
    }))
    .sort((a, b) => {
      const indexA = order.indexOf(a.type)
      const indexB = order.indexOf(b.type)
      return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB)
    })
})

const deviceTypeSeries = computed(() => {
  const series = deviceTypeCards.value.map(item => item.total)
  return series.length ? series : [0]
})

const deviceTypeOptions = computed(() => ({
  chart: {
    type: 'donut',
    background: 'transparent',
    foreColor: '#94a3b8',
    fontFamily: 'inherit'
  },
  labels: deviceTypeCards.value.map(item => item.type),
  colors: ['#22d3ee', '#34d399', '#a78bfa', '#fbbf24', '#fb7185'],
  legend: {
    position: 'bottom',
    labels: { colors: '#cbd5e1' },
    markers: { size: 6 }
  },
  stroke: { colors: ['#070b14'] },
  dataLabels: { enabled: false },
  tooltip: { theme: 'dark' },
  plotOptions: {
    pie: {
      donut: {
        size: '68%',
        labels: {
          show: true,
          total: { show: true, label: 'Total', color: '#e2e8f0' },
          value: { color: '#ffffff', fontWeight: 800 }
        }
      }
    }
  }
}))

const kpiCards = computed(() => {
  const devices = overview.value?.devices || {}
  const websocket = overview.value?.websocket || {}
  const database = overview.value?.database || {}
  const totals = report.value?.totals || {}

  return [
    {
      label: 'Uptime serveur',
      value: formatUptime(overview.value?.server?.uptimeSeconds),
      detail: `Node ${overview.value?.server?.nodeVersion || '—'}`,
      badge: 'UP',
      tone: 'emerald'
    },
    {
      label: 'Latence MongoDB',
      value: database.pingMs != null ? `${database.pingMs} ms` : '—',
      detail: database.status || '—',
      badge: 'DB',
      tone: 'cyan'
    },
    {
      label: 'Sockets actifs',
      value: websocket.connectedSockets ?? '—',
      detail: `${websocket.superAdminListeners || 0} listener(s) dev`,
      badge: 'WS',
      tone: 'violet'
    },
    {
      label: 'Devices en ligne',
      value: `${devices.online ?? '—'}/${devices.total ?? '—'}`,
      detail: `${devices.offline ?? 0} hors ligne`,
      badge: 'IOT',
      tone: 'emerald'
    },
    {
      label: 'Événements / min',
      value: eventsPerMinute.value,
      detail: `${events.value.length} stockés`,
      badge: 'LIVE',
      tone: 'amber'
    },
    {
      label: 'Tickets 30 j',
      value: totals.total ?? '—',
      detail: `${totals.averagePerDay ?? '—'} / jour`,
      badge: '30D',
      tone: 'cyan'
    }
  ]
})

const trendSeries = computed(() => {
  const trend = report.value?.ticketsTrend || []
  return [
    { name: 'Tickets créés', data: trend.map(item => item.total) },
    { name: 'Tickets traités', data: trend.map(item => item.done) }
  ]
})

const trendOptions = computed(() => ({
  chart: {
    type: 'area',
    background: 'transparent',
    foreColor: '#94a3b8',
    fontFamily: 'inherit',
    toolbar: { show: false }
  },
  colors: ['#22d3ee', '#34d399'],
  stroke: { curve: 'smooth', width: 2.5 },
  dataLabels: { enabled: false },
  fill: {
    type: 'gradient',
    gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.03, stops: [0, 90, 100] }
  },
  grid: { borderColor: 'rgba(148, 163, 184, 0.12)', strokeDashArray: 4 },
  xaxis: {
    categories: (report.value?.ticketsTrend || []).map(item => formatShortDate(item.date)),
    labels: { style: { colors: '#64748b', fontSize: '10px' } },
    axisBorder: { show: false },
    axisTicks: { show: false }
  },
  yaxis: {
    labels: { style: { colors: '#64748b', fontSize: '10px' } },
    min: 0
  },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    labels: { colors: '#cbd5e1' },
    markers: { size: 6 }
  },
  tooltip: { theme: 'dark' }
}))

const statusSeries = computed(() => (report.value?.statusBreakdown || []).map(item => item.count))
const statusOptions = computed(() => ({
  chart: {
    type: 'donut',
    background: 'transparent',
    foreColor: '#94a3b8',
    fontFamily: 'inherit'
  },
  labels: (report.value?.statusBreakdown || []).map(item => item.status),
  colors: ['#22d3ee', '#fbbf24', '#34d399', '#a78bfa', '#fb7185'],
  legend: {
    position: 'bottom',
    labels: { colors: '#cbd5e1' },
    markers: { size: 6 }
  },
  stroke: { colors: ['#070b14'] },
  dataLabels: { enabled: false },
  tooltip: { theme: 'dark' },
  plotOptions: {
    pie: {
      donut: {
        size: '66%',
        labels: {
          show: true,
          total: { show: true, label: 'Total', color: '#e2e8f0' },
          value: { color: '#ffffff', fontWeight: 800 }
        }
      }
    }
  }
}))

const agencySeries = computed(() => [{
  name: 'Tickets',
  data: (report.value?.topAgencies || []).map(item => item.count)
}])

const agencyOptions = computed(() => ({
  chart: {
    type: 'bar',
    background: 'transparent',
    foreColor: '#94a3b8',
    fontFamily: 'inherit',
    toolbar: { show: false }
  },
  colors: ['#34d399'],
  plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
  dataLabels: { enabled: false },
  grid: { borderColor: 'rgba(148, 163, 184, 0.12)', strokeDashArray: 4 },
  xaxis: {
    categories: (report.value?.topAgencies || []).map(item => item.agencyName),
    labels: { style: { colors: '#64748b', fontSize: '10px' } }
  },
  yaxis: { labels: { style: { colors: '#cbd5e1', fontSize: '10px' } } },
  tooltip: { theme: 'dark' }
}))

const hourlySeries = computed(() => [{
  name: 'Tickets',
  data: (report.value?.hourlyDistribution || []).map(item => item.count)
}])

const hourlyOptions = computed(() => ({
  chart: {
    type: 'bar',
    background: 'transparent',
    foreColor: '#94a3b8',
    fontFamily: 'inherit',
    toolbar: { show: false }
  },
  colors: ['#fbbf24'],
  plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
  dataLabels: { enabled: false },
  grid: { borderColor: 'rgba(148, 163, 184, 0.12)', strokeDashArray: 4 },
  xaxis: {
    categories: (report.value?.hourlyDistribution || []).map(item => `${String(item.hour).padStart(2, '0')}h`),
    labels: { style: { colors: '#64748b', fontSize: '9px' } }
  },
  yaxis: { labels: { style: { colors: '#64748b', fontSize: '10px' } } },
  tooltip: { theme: 'dark' }
}))

/* ==========================================================
   EXPORT
   ========================================================== */

const exportReportCsv = () => {
  if (!report.value) return

  const header = ['Date', 'Total', 'Traités', 'En attente', 'Appelés', 'Ignorés', 'Archivés']
  const rows = (report.value.ticketsTrend || []).map(item => [
    item.date,
    item.total,
    item.done,
    item.waiting,
    item.called,
    item.skipped,
    item.archived
  ])

  const summary = [
    [],
    ['Résumé'],
    ['Tickets créés', report.value.totals?.total ?? 0],
    ['Moyenne / jour', report.value.totals?.averagePerDay ?? 0],
    ['Taux de traitement (%)', report.value.totals?.completionRate ?? 0],
    ['Tickets traités', report.value.totals?.done ?? 0],
    ['Tickets en attente', report.value.totals?.waiting ?? 0]
  ]

  const csv = [header, ...rows, ...summary]
    .map(line => line.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\n')

  const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `medialink-developer-report-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

/* ==========================================================
   LIFECYCLE
   ========================================================== */

onMounted(async () => {
  await loadAll()
  setupSocket()

  overviewTimer = setInterval(() => {
    if (!document.hidden) loadOverview().catch(() => {})
  }, 10000)

  reportTimer = setInterval(() => {
    if (!document.hidden) loadReport().catch(() => {})
  }, 60000)

  eventsFallbackTimer = setInterval(() => {
    if (!document.hidden && !socketConnected.value) loadEvents().catch(() => {})
  }, 15000)

  clockTimer = setInterval(() => {
    now.value = Date.now()
  }, 2000)
})

onUnmounted(() => {
  if (overviewTimer) clearInterval(overviewTimer)
  if (reportTimer) clearInterval(reportTimer)
  if (eventsFallbackTimer) clearInterval(eventsFallbackTimer)
  if (clockTimer) clearInterval(clockTimer)

  if (socket.value) {
    socket.value.removeAllListeners()
    socket.value.disconnect()
    socket.value = null
  }
})
</script>

<style scoped>
.dev-icon :deep(svg),
.dev-icon svg {
  display: block;
  width: 100%;
  height: 100%;
}

.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.25); border-radius: 999px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(148, 163, 184, 0.45); }

pre { white-space: pre-wrap; word-break: break-word; }
</style>
